import React, { useEffect, useState } from "react";
import { DetailsSection } from "components/DetailsSection";
import { AdditionalServicesSection } from "components/AdditionalServicesSection";
import { MapToLine } from "helpers/Helpers";
import { getAdditionalService } from "helpers/BookingConversionHelper";
import { BookingHero } from "components/BookingHero";
import { BookingActions } from "components/BookingActions";
import { BookingDate } from "components/BookingDate";
import { LoadingBar } from "components/LoadingBar";
import config from "clientappsetting.json";
import { RouteComponentProps, useHistory } from "react-router";
import { WorkDetailsSection } from "components/WorkDetailsSection";
import { ModalCentered } from "components/generic/ModalCentered";
import { Modal, Button } from "react-bootstrap";
import dayjs from "dayjs";
import { Icons } from "components/icons/Icons";
import { BookingStatus, BookingSummaryInformation } from "generated/types/types";
import { useTranslation } from "react-i18next";
import { ApiUrlResolver } from "utils/ApiUrlResolver";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";

interface MyParams {
    encryptedBookingId: string;
}

interface Props extends RouteComponentProps<MyParams> {
    populateNewBookingState: (existingBooking: BookingSummaryInformation) => void;
    micrositePin: string;
}

export const ExistingBooking: React.FC<Props> = (props: Props) => {
    const [bookingSummaryInfo, setBookingSummaryInfo] = useState<BookingSummaryInformation>();
    const encryptedBookingId: string = props.match.params.encryptedBookingId;
    const [cancelShow, setCancelShow] = React.useState(false);
    const [cancelRerequestShow, setCancelRerequestShow] = React.useState(false);
    const [acceptOfferedDateShow, setAcceptOfferedDateShow] = React.useState(false);
    const [actionsDisabled, setActionsDisabled] = React.useState(false);
    const history = useHistory();
    const { t } = useTranslation();
    const countryCode = useSelector((state: RootState) => state.country.countryCode);

    useEffect(() => {
        fetch(ApiUrlResolver.getWebApiUrl(countryCode) + "/Booking/" + encryptedBookingId)
            .then(response => response.json())
            .then(data => {
                setBookingSummaryInfo(data);
            });
    }, [encryptedBookingId]);

    if (!bookingSummaryInfo?.bookingDetails) {
        return <LoadingBar fullscreen={true} />;
    }

    const cancelBooking = () => {
        setActionsDisabled(true);
        fetch(ApiUrlResolver.getWebApiUrl(countryCode) + "/Booking/CancelBooking/" + encryptedBookingId, { method: "post" }).then(() => {
            setCancelShow(false);
            window.location.reload();
        });
    };

    const cancelBookingRerequest = () => {
        setActionsDisabled(true);
        fetch(ApiUrlResolver.getWebApiUrl(countryCode) + "/Booking/CancelBooking/" + encryptedBookingId + "?delayMessages=true", { method: "post" }).then(() => {
            props.populateNewBookingState(bookingSummaryInfo); // Populate the booking state from the booking we just cancelled
            setCancelRerequestShow(false);
            history.push(`/${props.micrositePin}`);
        });
    };

    const acceptOfferedDate = () => {
        setActionsDisabled(true);
        fetch(ApiUrlResolver.getWebApiUrl(countryCode) + "/Booking/AcceptBookingOfferedDate/" + encryptedBookingId, { method: "post" }).then(() => {
            setAcceptOfferedDateShow(false);
            window.location.reload();
        });
    };

    //TODO: merge modal contents into a single function
    const getCancelContents = () => {
        return (
            <Modal.Body>
                <h1>{t("booking_actions_cancel")}</h1>
                <h2>{t("booking_actions_cancel_desc")}</h2>
                <Button size="lg" onClick={() => cancelBooking()} disabled={actionsDisabled}>
                   {t("booking_actions_yes")}
                </Button>
                <p className="link-muted" onClick={() => setCancelShow(false)}>
                   {t("booking_actions_no")}
                </p>
            </Modal.Body>
        );
    };

    const getCancelRerequestContents = () => {
        return (
            <Modal.Body>
                {bookingSummaryInfo.bookingDetails.bookingStatus === BookingStatus.Declined ? (
                    <>
                        <h1>{t("booking_actions_rerequest")}</h1>
                        <h2>{t("booking_actions_rerequest_desc")}</h2>
                    </>
                ) : (
                    <>
                        <h1>{t("booking_actions_cancel_rerequest")}</h1>
                        <h2>{t("booking_actions_cancel_rerequest_desc")}</h2>
                    </>
                )}

                <Button size="lg" onClick={() => cancelBookingRerequest()} disabled={actionsDisabled}>
                    {t("booking_actions_yes")}
                </Button>
                <p className="link-muted" onClick={() => setCancelRerequestShow(false)}>
                    {t("booking_actions_no")}
                </p>
            </Modal.Body>
        );
    };

    const getAcceptOfferContents = () => {
        // Why are we constructing the dialog contents when it's not needed?
        const offeredDate = bookingSummaryInfo.bookingDetails.offeredDate ?? new Date();

        return (
            <Modal.Body>
                <h1>{t("booking_actions_alternate_date")}</h1>
                <h2>{t("booking_actions_accept_new_date")}{dayjs(offeredDate).format(`DD MMM YYYY [${t("date_picker_at")}] HH:mm`)}?</h2>
                <Button size="lg" onClick={() => acceptOfferedDate()} disabled={actionsDisabled}>
                    {t("booking_actions_yes")}
                </Button>
                <p className="link-muted" onClick={() => setAcceptOfferedDateShow(false)}>
                    {t("booking_actions_no")}
                </p>
            </Modal.Body>
        );
    };

    const GetSections = () => {
        const sections: React.ReactNode[] = [];

        if (bookingSummaryInfo.bookingDetails.bookingStatus === BookingStatus.Declined) {
            sections.push(
                <DetailsSection
                    key="DetailsSection0"
                    iconComponent={Icons.QuestionIcon}
                    heading={t("booking_actions_reason_decline")}
                    lines={[{ text: bookingSummaryInfo.bookingDetails.bookingDeclinedReason }]}
                />
            );
        }
        sections.push(
            <DetailsSection
                key={"DetailsSection1"}
                iconComponent={Icons.GarageIcon}
                heading={t("booking_actions_garage")}
                lines={[
                    { isHighlighted: true, text: bookingSummaryInfo.serviceCentreDetails.name },
                    ...MapToLine(bookingSummaryInfo.serviceCentreDetails.addressLines),
                    { isHighlighted: true, text: t("booking_actions_tel") + bookingSummaryInfo.serviceCentreDetails.phoneNumber },
                ]}
            />
        );
        sections.push(<WorkDetailsSection key="workDetails" workDetails={bookingSummaryInfo.workDetails} />);
        sections.push(
            <AdditionalServicesSection
                key={"AdditionalServicesSection"}
                additionalServiceType={getAdditionalService(bookingSummaryInfo.bookingDetails)}
                collectionAddressLines={bookingSummaryInfo.bookingDetails.collectionAddressLines}
                additionalNotes={bookingSummaryInfo.workDetails.additionalNotes}
            />
        );
        sections.push(
            <DetailsSection
                key={"DetailsSection2"}
                iconComponent={Icons.VehicleIcon}
                heading={t("booking_actions_vehicle")}
                lines={[
                    { prefix: t("booking_actions_reg_no"), text: bookingSummaryInfo.vehicleDetails.registrationNumber },
                    { text: bookingSummaryInfo.vehicleDetails.make + " " + bookingSummaryInfo.vehicleDetails.model + " " + bookingSummaryInfo.vehicleDetails.derivative },
                ]}
            />
        );

        return sections;
    };

    const firstPreferenceDate = bookingSummaryInfo.bookingDetails.firstPreferenceDate;
    const secondPreferenceDate = bookingSummaryInfo.bookingDetails.secondPreferenceDate;
    const thirdPreferenceDate = bookingSummaryInfo.bookingDetails.thirdPreferenceDate;

    return (
        <div>
            <BookingHero
                bookingId={bookingSummaryInfo.bookingDetails.bookingId}
                bookingStatus={bookingSummaryInfo.bookingDetails.bookingStatus}
                regNo={bookingSummaryInfo.vehicleDetails.registrationNumber}
            />
            <div className="row details-body">
                <div className="col">
                    <BookingDate
                        bookingDate={bookingSummaryInfo.bookingDetails.date}
                        offeredDate={bookingSummaryInfo.bookingDetails.offeredDate}
                        userRequestedDates={[firstPreferenceDate, secondPreferenceDate, thirdPreferenceDate]}
                        bookingStatus={bookingSummaryInfo.bookingDetails.bookingStatus}
                    />
                    {GetSections()}
                    <BookingActions
                        bookingStatus={bookingSummaryInfo.bookingDetails.bookingStatus}
                        onCancel={() => setCancelShow(true)}
                        onCancelRerequest={() => setCancelRerequestShow(true)}
                        onAcceptOfferedDate={() => setAcceptOfferedDateShow(true)}
                        onRequestNewBooking={() => {
                            props.populateNewBookingState(bookingSummaryInfo); // Populate the new booking's state from this existing booking
                            history.push(`/${props.micrositePin}`);
                        }}
                    />
                </div>
            </div>
            <ModalCentered size="sm" show={cancelShow} onHide={() => setCancelShow(false)}>
                <div className="custom-dialog">
                    <Modal.Header closeButton />
                    {getCancelContents()}
                </div>
            </ModalCentered>
            <ModalCentered size="sm" show={cancelRerequestShow} onHide={() => setCancelRerequestShow(false)}>
                <div className="custom-dialog">
                    <Modal.Header closeButton />
                    {getCancelRerequestContents()}
                </div>
            </ModalCentered>
            <ModalCentered size="sm" show={acceptOfferedDateShow} onHide={() => setAcceptOfferedDateShow(false)}>
                <div className="custom-dialog">
                    <Modal.Header closeButton />
                    {getAcceptOfferContents()}
                </div>
            </ModalCentered>
        </div>
    );
};
